import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { purchaseInvoicesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-PROC-05 — PI detail. Shows the procure-to-pay chain (PO → GRN → PI) and the
 * journal entry generated at post (DR Inventory + DR GST / CR AP).
 */
export function PurchaseInvoiceDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canPost = useAuth(s => s.hasPermission("procurement.pi.post"));
  const { taxLabel, isGst } = useTaxRegime();
  const [postOpen, setPostOpen] = useState(false);

  const query = useQuery({
    queryKey: ["purchase-invoice", id],
    queryFn: () => purchaseInvoicesApi.get(id),
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => purchaseInvoicesApi.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-invoice", id] });
      qc.invalidateQueries({ queryKey: ["purchase-invoices"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <CircularProgress />;
  const pi = query.data;
  const isDraft = pi.status === "Draft";

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{pi.documentNo}</Typography>
          <Chip label={pi.status} color={STATUS_COLOR[pi.status] ?? "default"} size="small" />
          {pi.journalEntryId && (
            <Chip
              icon={<AccountBalanceIcon />}
              label="GL posted"
              color="success"
              variant="outlined"
              component={RouterLink as any}
              to={`/accounting/journals/${pi.journalEntryId}`}
              clickable
            />
          )}
        </Stack>
        {isDraft && canPost && (
          <Button variant="contained" color="success" startIcon={<PostAddIcon />}
            onClick={() => setPostOpen(true)}>
            Post PI
          </Button>
        )}
      </Stack>

      {/* Procure-to-pay chain banner — direct PIs skip the GRN segment */}
      <Alert severity={pi.source === "Direct" ? "warning" : "info"} sx={{ mb: 2 }}>
        {pi.source === "Direct" ? <b>Direct Purchase Invoice — </b> : <b>Procure-to-pay chain: </b>}
        Vendor <b>{pi.vendorName}</b>
        {pi.grnId && pi.grnNo && (
          <> · GRN&nbsp;<RouterLink to={`/procurement/grns/${pi.grnId}`}>{pi.grnNo}</RouterLink></>
        )}
        {" → PI "}<b>{pi.documentNo}</b>
        {pi.vendorInvoiceNo && (<> · Vendor invoice <b>{pi.vendorInvoiceNo}</b>
          {pi.vendorInvoiceDate && ` (${new Date(pi.vendorInvoiceDate).toLocaleDateString()})`}</>)}
      </Alert>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">Header</Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 1 }}>
          <Stack>
            <Typography variant="caption" color="text.secondary">PI date</Typography>
            <Typography>{new Date(pi.documentDate).toLocaleDateString()}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Subtotal</Typography>
            <Typography>{inr.format(pi.subTotal)}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">{taxLabel}</Typography>
            <Typography>{inr.format(pi.taxAmount)}</Typography>
          </Stack>
          {pi.discountAmount > 0 && (
            <Stack>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography color="error">− {inr.format(pi.discountAmount)}</Typography>
            </Stack>
          )}
          <Stack>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" color="primary">{inr.format(pi.totalAmount)}</Typography>
          </Stack>
        </Stack>
        {pi.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{pi.notes}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines (3-way matched against GRN)</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Line total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pi.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell align="right">{l.qty}</TableCell>
                <TableCell align="right">{inr.format(l.rate)}</TableCell>
                <TableCell align="right">{inr.format(l.taxAmount)}</TableCell>
                <TableCell align="right">{inr.format(l.discountAmount)}</TableCell>
                <TableCell align="right">{inr.format(l.lineTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end">
          <Stack>
            <Typography variant="caption" color="text.secondary">Invoiced total</Typography>
            <Typography variant="h6" color="primary">{inr.format(pi.totalAmount)}</Typography>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1}>
        <Button onClick={() => navigate("/procurement/purchase-invoices")}>Back to list</Button>
      </Stack>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Post Purchase Invoice {pi.documentNo}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting writes a balanced JournalEntry:
            <br />· <b>DR 1300 Inventory</b> {inr.format(pi.subTotal)}
            {pi.taxAmount > 0 && (
              <><br />· <b>DR {isGst ? "1410 GST" : "1411 VAT"} Input</b> {inr.format(pi.taxAmount)}</>
            )}
            <br />· <b>CR 2100 Accounts Payable</b> {inr.format(pi.totalAmount)}
            <br /><br />Posted journals are immutable — corrections require a reversing entry.
          </DialogContentText>
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={postMutation.isPending}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}>
            Post & write JE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
